/* eslint-disable @typescript-eslint/no-unused-vars */
import { v4 as uuidv4 } from 'uuid';

import { ArticleInstancePlacementSourceType } from 'api/models';
import { getMostDistinctPastelColorHex } from 'app/modules/wallforming/components/utils/pastelUtils';

import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';
import type { ArticleGroupSliceState } from 'app/modules/wallforming/components/state-management/placement/states/articleGroupSliceState';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { StateCreator } from 'zustand';

function getSolverGroupKey(instance: ArticleInstance | undefined): string | null {
    if (!instance) {
        return null;
    }

    if (instance.sourceGroupInstanceId) {
        return `solver-instance:${instance.sourceGroupInstanceId}`;
    }

    if (instance.sourceType === ArticleInstancePlacementSourceType.UnsavedGroup && instance.sourceGroupId) {
        return `unsaved-source:${instance.sourceGroupId}`;
    }

    if (instance.sourceType === ArticleInstancePlacementSourceType.SavedGroup && instance.sourceSavedGroupPrefabId) {
        return `saved-source:${instance.sourceSavedGroupPrefabId}`;
    }

    return null;
}

function getArticleInstance(state: PlacementState, instanceId: string): ArticleInstance | undefined {
    for (const article of Object.values(state.articles)) {
        const instance = article.articleInstances[instanceId];
        if (instance) {
            return instance;
        }
    }

    return undefined;
}

function getSolverGroupMemberInstanceIds(state: PlacementState, instanceId: string): string[] {
    const solverGroupKey = getSolverGroupKey(getArticleInstance(state, instanceId));
    if (!solverGroupKey) {
        return [instanceId];
    }

    const memberIds: string[] = [];
    for (const article of Object.values(state.articles)) {
        for (const [memberId, memberInstance] of Object.entries(article.articleInstances)) {
            if (!memberInstance.isLocked && getSolverGroupKey(memberInstance) === solverGroupKey) {
                memberIds.push(memberId);
            }
        }
    }

    return memberIds.length > 0 ? memberIds : [instanceId];
}

function getGroupColors(state: PlacementState): string[] {
    return Object.values(state.articleGroups).map((group) => group.color);
}

export const createArticleGroupSlice: StateCreator<PlacementState, [], [], ArticleGroupSliceState> = (set, get) => ({
    articleGroups: {},
    instanceToGroupMap: {},
    isGroupPanelOpen: false,
    editingGroupId: null,
    pendingGroupName: '',
    pendingGroupColor: getMostDistinctPastelColorHex([]),

    createArticleGroup: (name, members, color) => {
        if (members.length < 2) {
            return undefined;
        }


        const groupId = uuidv4();
        const trimmedName = name.trim() || `Group ${Object.keys(get().articleGroups).length + 1}`;
        // Pick a color that is maximally separated from all existing group colors.
        const existingColors = getGroupColors(get());
        const groupColor = color || getMostDistinctPastelColorHex(existingColors);

        set((state) => {
            // Remove members from any existing groups
            const updatedGroups = { ...state.articleGroups };
            const updatedMap = { ...state.instanceToGroupMap };

            for (const member of members) {
                const existingGroupId = updatedMap[member.instanceId];
                if (existingGroupId && updatedGroups[existingGroupId]) {
                    const existingGroup = updatedGroups[existingGroupId];
                    const filtered = existingGroup.members.filter((m) => m.instanceId !== member.instanceId);
                    if (filtered.length < 2) {
                        // group would have less than 2 members, dissolve it
                        for (const m of filtered) {
                            delete updatedMap[m.instanceId];
                        }
                        delete updatedGroups[existingGroupId];
                    } else {
                        updatedGroups[existingGroupId] = { ...existingGroup, members: filtered };
                    }
                }
            }

            // Create the new group
            updatedGroups[groupId] = {
                id: groupId,
                name: trimmedName,
                color: groupColor,
                members: [...members]
            };

            // Update reverse mapping
            for (const member of members) {
                updatedMap[member.instanceId] = groupId;
            }

            return {
                articleGroups: updatedGroups,
                instanceToGroupMap: updatedMap,
                isGroupPanelOpen: false,
                pendingGroupName: '',
                pendingGroupColor: getMostDistinctPastelColorHex(Object.values(updatedGroups).map((group) => group.color), groupColor)
            };
        });

        return groupId;
    },

    removeArticleGroup: (groupId) => {
        set((state) => {
            const group = state.articleGroups[groupId];
            if (!group) {
                return state;
            }

            const { [groupId]: _, ...restGroups } = state.articleGroups;
            const updatedMap = { ...state.instanceToGroupMap };

            for (const member of group.members) {
                if (updatedMap[member.instanceId] === groupId) {
                    delete updatedMap[member.instanceId];
                }
            }

            return {
                articleGroups: restGroups,
                instanceToGroupMap: updatedMap
            };
        });
    },

    renameArticleGroup: (groupId, name) => {
        set((state) => {
            const group = state.articleGroups[groupId];
            if (!group) {
                return state;
            }

            return {
                articleGroups: {
                    ...state.articleGroups,
                    [groupId]: { ...group, name }
                }
            };
        });
    },

    setArticleGroupColor: (groupId, color) => {
        set((state) => {
            const group = state.articleGroups[groupId];
            if (!group) {
                return state;
            }

            return {
                articleGroups: {
                    ...state.articleGroups,
                    [groupId]: { ...group, color }
                }
            };
        });
    },

    getGroupByInstanceId: (instanceId) => {
        const state = get();
        const groupId = state.instanceToGroupMap[instanceId];
        if (!groupId) {
            return undefined;
        }
        return state.articleGroups[groupId];
    },

    getGroupMemberInstanceIds: (instanceId) => {
        const state = get();
        const groupId = state.instanceToGroupMap[instanceId];
        if (!groupId) {
            return getSolverGroupMemberInstanceIds(state, instanceId);
        }

        const group = state.articleGroups[groupId];
        if (!group) {
            return getSolverGroupMemberInstanceIds(state, instanceId);
        }

        return group.members.map((m) => m.instanceId);
    },

    handleGroupSelections: (selectedInstanceIds) => {
        const state = get();
        const expandedSelections: Record<string, number | undefined> = {};
        const processedGroups = new Set<string>();

        for (const instanceId of selectedInstanceIds) {
            const groupId = state.instanceToGroupMap[instanceId];

            if (!groupId) {
                for (const memberId of getSolverGroupMemberInstanceIds(state, instanceId)) {
                    expandedSelections[memberId] = undefined;
                }
                continue;
            }

            if (processedGroups.has(groupId)) {
                expandedSelections[instanceId] = undefined;
                continue;
            }

            const group = state.articleGroups[groupId];
            if (!group) {
                expandedSelections[instanceId] = undefined;
                continue;
            }

            processedGroups.add(groupId);

            // Add all group members to selection
            for (const member of group.members) {
                expandedSelections[member.instanceId] = undefined;
            }
        }

        return expandedSelections;
    },

    setIsGroupPanelOpen: (open) => set({ isGroupPanelOpen: open, ...(!open ? { editingGroupId: null } : {}) }),

    openEditGroupPanel: (groupId) => {
        const group = get().articleGroups[groupId];
        if (!group) {
            return;
        }
        set({
            isGroupPanelOpen: true,
            editingGroupId: groupId,
            pendingGroupName: group.name,
            pendingGroupColor: group.color
        });
    },

    updateArticleGroup: (groupId, name, members, color) => {
        set((state) => {
            const group = state.articleGroups[groupId];
            if (!group) {
                return state;
            }

            const updatedGroups = { ...state.articleGroups };
            const updatedMap = { ...state.instanceToGroupMap };

            // Remove previous members from the map
            for (const member of group.members) {
                if (updatedMap[member.instanceId] === groupId) {
                    delete updatedMap[member.instanceId];
                }
            }

            // New members may belong to other groups — remove them there first
            for (const member of members) {
                const existingGroupId = updatedMap[member.instanceId];
                if (existingGroupId && existingGroupId !== groupId && updatedGroups[existingGroupId]) {
                    const existingGroup = updatedGroups[existingGroupId];
                    const filtered = existingGroup.members.filter((m) => m.instanceId !== member.instanceId);
                    if (filtered.length < 2) {
                        for (const m of filtered) {
                            delete updatedMap[m.instanceId];
                        }
                        delete updatedGroups[existingGroupId];
                    } else {
                        updatedGroups[existingGroupId] = { ...existingGroup, members: filtered };
                    }
                }
            }

            // Apply updates to this group
            const trimmedName = name.trim() || group.name;
            updatedGroups[groupId] = { ...group, name: trimmedName, color, members: [...members] };

            for (const member of members) {
                updatedMap[member.instanceId] = groupId;
            }

            return {
                articleGroups: updatedGroups,
                instanceToGroupMap: updatedMap,
                isGroupPanelOpen: false,
                editingGroupId: null,
                pendingGroupName: '',
                pendingGroupColor: getMostDistinctPastelColorHex(Object.values(updatedGroups).map((updatedGroup) => updatedGroup.color), color)
            };
        });
    },

    setPendingGroupName: (name) => set({ pendingGroupName: name }),

    setPendingGroupColor: (color) => set({ pendingGroupColor: color }),

    removeInstancesFromGroups: (instanceIds) => {
        if (instanceIds.length === 0) {
            return;
        }

        set((state) => {
            const updatedGroups = { ...state.articleGroups };
            const updatedMap = { ...state.instanceToGroupMap };
            let hasChanges = false;

            const affectedGroupIds = new Set<string>();
            for (const instanceId of instanceIds) {
                const groupId = updatedMap[instanceId];
                if (groupId) {
                    affectedGroupIds.add(groupId);
                    delete updatedMap[instanceId];
                    hasChanges = true;
                }
            }

            const instanceIdSet = new Set(instanceIds);
            for (const groupId of affectedGroupIds) {
                const group = updatedGroups[groupId];
                if (!group) {
                    continue;
                }

                const remaining = group.members.filter((m) => !instanceIdSet.has(m.instanceId));
                if (remaining.length < 2) {
                    // Dissolve the group
                    for (const m of remaining) {
                        delete updatedMap[m.instanceId];
                    }
                    delete updatedGroups[groupId];
                } else {
                    updatedGroups[groupId] = { ...group, members: remaining };
                }
            }

            if (!hasChanges) {
                return state;
            }

            return {
                articleGroups: updatedGroups,
                instanceToGroupMap: updatedMap
            };
        });
    }
});
